import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import Checkbox from "../../../../shared/ui/form/input/Checkbox";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import { StripeConnectPanel } from "../../../payments";
import { useCenterPaymentSettings, useDisconnectCenterStripe, useStartCenterStripeConnect, useUpdateCenterPaymentSettings } from "../../../center";

interface Props {
    centerId: string;
}

/**
 * How the centre charges for the patients it holds.
 *
 * With Payments on, these replace each therapist's own settings for the
 * centre's patients: links go to the centre's Stripe account, new payments
 * start from the centre's line, and the centre decides whether they are
 * chased. A therapist's own patients stay on the therapist's settings.
 *
 * Until the centre connects Stripe, links for its patients are refused rather
 * than sent to somebody's personal account.
 */
export default function CenterPaymentSettingsCard({ centerId }: Props) {
    const { t } = useTranslation();
    const { settings, loading } = useCenterPaymentSettings(centerId);
    const update = useUpdateCenterPaymentSettings(centerId);
    const connect = useStartCenterStripeConnect(centerId);
    const disconnect = useDisconnectCenterStripe(centerId);

    const [concept, setConcept] = useState("");
    const [amount, setAmount] = useState("");
    const [reminders, setReminders] = useState(true);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filled from the server once it answers; afterwards the form is the
    // administrator's until they save.
    useEffect(() => {
        if (!settings) return;
        setConcept(settings.default_payment_concept ?? "");
        setAmount(settings.default_payment_amount ?? "");
        setReminders(settings.payment_reminders_enabled);
    }, [settings]);

    const save = async () => {
        setSaved(false);
        setError(null);
        const trimmed = concept.trim();
        const parsed = amount.trim() === "" ? null : Number(amount.replace(",", "."));
        if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) {
            setError(t("center.paymentSettings.errorAmount"));
            return;
        }
        try {
            await update.mutateAsync({
                default_payment_concept: trimmed === "" ? null : trimmed,
                default_payment_amount: parsed === null ? null : parsed.toFixed(2),
                payment_reminders_enabled: reminders,
            });
            setSaved(true);
        } catch {
            setError(t("center.paymentSettings.errorGeneric"));
        }
    };

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h2 className="mb-1 text-lg font-semibold text-content-primary">
                {t("center.paymentSettings.title")}
            </h2>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                {t("center.paymentSettings.subtitle")}
            </p>

            {settings?.stripe.available !== false && (
                <div className="mb-6">
                    <StripeConnectPanel
                        status={settings?.stripe}
                        isLoading={loading}
                        onConnect={() => connect.mutate()}
                        connecting={connect.isPending}
                        onRevoke={() => disconnect.mutate()}
                        revoking={disconnect.isPending}
                        description={t("center.paymentSettings.stripeDescription")}
                    />
                </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <Label htmlFor="center-default-concept">
                        {t("center.paymentSettings.defaultConcept")}
                    </Label>
                    <Input
                        id="center-default-concept"
                        value={concept}
                        onChange={(e) => setConcept(e.target.value)}
                        placeholder={t("center.paymentSettings.defaultConceptPlaceholder")}
                    />
                </div>
                <div>
                    <Label htmlFor="center-default-amount">
                        {t("center.paymentSettings.defaultAmount")}
                    </Label>
                    <Input
                        id="center-default-amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                    />
                </div>
            </div>

            <div className="mt-4">
                <Checkbox
                    id="center-payment-reminders"
                    checked={reminders}
                    onChange={setReminders}
                    label={t("center.paymentSettings.reminders")}
                />
            </div>

            {error && (
                <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
            {saved && !error && (
                <p role="status" className="mt-4 text-sm text-green-600 dark:text-green-400">
                    {t("center.paymentSettings.saved")}
                </p>
            )}

            <div className="mt-5">
                <Button onClick={save} loading={update.isPending} disabled={loading}>
                    {t("common.save")}
                </Button>
            </div>
        </section>
    );
}
